import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Global DOM-based replacement for `window.confirm()`.
 *
 * Usage:
 *   import { confirm } from "@/components/ConfirmDialog";
 *   if (!(await confirm("Delete this item?"))) return;
 *
 *   // or with options:
 *   const ok = await confirm({
 *     title: "Delete recipe?",
 *     description: `Remove "${name}"? This cannot be undone.`,
 *     confirmText: "Delete",
 *     cancelText: "Keep",
 *     variant: "destructive",     // styles the confirm button red
 *   });
 *
 * Rules:
 *   - The <ConfirmDialogHost /> must be mounted exactly once (App.js).
 *   - `confirm()` returns Promise<boolean>. Resolves true on Confirm,
 *     false on Cancel / ESC / backdrop click / dialog dismiss.
 *   - Nested calls are queued: only the most recent call is visible; the
 *     previous one resolves to false when superseded.
 *   - data-testids: confirm-dialog, confirm-dialog-title,
 *     confirm-dialog-description, confirm-dialog-cancel,
 *     confirm-dialog-confirm.
 */

// Module-level state for the singleton dialog. Multiple hosts (e.g., if a page
// remounts) will all subscribe here; only the currently-mounted one will render
// the visible dialog because state changes fan out to every subscriber.
let subscribers = [];
let currentResolver = null;

function notify(payload) {
  subscribers.forEach((fn) => fn(payload));
}

function resolveAndClose(value) {
  const r = currentResolver;
  currentResolver = null;
  notify({ open: false, opts: {} });
  if (r) r(value);
}

export function confirm(options) {
  const opts =
    typeof options === "string"
      ? { description: options }
      : (options || {});
  return new Promise((resolve) => {
    // If a dialog is already open, resolve the previous one as false first.
    if (currentResolver) {
      const prev = currentResolver;
      currentResolver = null;
      prev(false);
    }
    currentResolver = resolve;
    notify({ open: true, opts });
  });
}

export function ConfirmDialogHost() {
  const [state, setState] = useState({ open: false, opts: {} });

  useEffect(() => {
    subscribers.push(setState);
    return () => {
      subscribers = subscribers.filter((s) => s !== setState);
    };
  }, []);

  const {
    title = "Are you sure?",
    description,
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "default", // "default" | "destructive"
  } = state.opts || {};

  const confirmBtnClass =
    variant === "destructive"
      ? "bg-red-600 hover:bg-red-500 text-white"
      : "bg-emerald-600 hover:bg-emerald-500 text-white";

  return (
    <AlertDialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) resolveAndClose(false);
      }}
    >
      <AlertDialogContent
        className="bg-zinc-900 border-zinc-700 text-zinc-100"
        data-testid="confirm-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle
            className="text-white"
            data-testid="confirm-dialog-title"
          >
            {title}
          </AlertDialogTitle>
          {description && (
            <AlertDialogDescription
              className="text-zinc-400 whitespace-pre-wrap"
              data-testid="confirm-dialog-description"
            >
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white"
            onClick={() => resolveAndClose(false)}
            data-testid="confirm-dialog-cancel"
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            className={confirmBtnClass}
            onClick={() => resolveAndClose(true)}
            data-testid="confirm-dialog-confirm"
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
